import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Chip
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { getAssignedInfluencers, getCompletedInfluencers, updateInfluencerStatus } from '../services/api';
import { ContentCopy, Email, Visibility, Download } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

function TabPanel({ children, value, index }) {
    return (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

function AdminInfluencers({ user }) {
    const [tabValue, setTabValue] = useState(0);
    const [assignedInfluencers, setAssignedInfluencers] = useState([]);
    const [completedInfluencers, setCompletedInfluencers] = useState([]);
    const [inProgressInfluencers, setInProgressInfluencers] = useState([]);
    const [notInterestedInfluencers, setNotInterestedInfluencers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });
    const [assignedSelectedRows, setAssignedSelectedRows] = useState([]);
    const [completedSelectedRows, setCompletedSelectedRows] = useState([]);
    const [emailModalOpen, setEmailModalOpen] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [emailSending, setEmailSending] = useState(false);
    const [selectedInfluencerId, setSelectedInfluencerId] = useState('');
    const [statusModalOpen, setStatusModalOpen] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [statusRemark, setStatusRemark] = useState('');
    const [currentInfluencerId, setCurrentInfluencerId] = useState('');
    const [languageFilter, setLanguageFilter] = useState('');
    const [stateFilter, setStateFilter] = useState('');
    const [mobileFilter, setMobileFilter] = useState('');
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchAllInfluencers();
    }, []);

    const fetchAllInfluencers = async () => {
        setLoading(true);
        try {
            const [assignedRes, completedRes] = await Promise.all([
                getAssignedInfluencers(),
                getCompletedInfluencers()
            ]);

            setAssignedInfluencers(assignedRes.data?.data || []);
            setCompletedInfluencers(completedRes.data?.data || []);

            await fetchInProgressInfluencers();
            await fetchNotInterestedInfluencers();
        } catch (error) {
            console.error('Fetch error:', error);
            showAlert('Failed to fetch influencers', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchInProgressInfluencers = async () => {
        try {
            const { default: api } = await import('../services/api');
            const response = await api.get('/existing-influencers/in-progress');
            setInProgressInfluencers(response.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch in-progress influencers:', error);
        }
    };

    const fetchNotInterestedInfluencers = async () => {
        try {
            const { default: api } = await import('../services/api');
            const response = await api.get('/existing-influencers/not-interested');
            setNotInterestedInfluencers(response.data?.data || []);
        } catch (error) {
            console.error('Failed to fetch not-interested influencers:', error);
        }
    };

    const handleStatusChange = async (id, newStatus) => {
        try {
            await updateInfluencerStatus(id, newStatus);
            showAlert(`Status updated to ${newStatus}`, 'success');
            fetchAllInfluencers();
        } catch (error) {
            showAlert('Failed to update status', 'error');
        }
    };

    const handleStatusUpdate = async (id, status) => {
        try {
            const { default: api } = await import('../services/api');
            await api.put(`/existing-influencers/${id}/status`, { status: status });
            showAlert(`Status updated to ${status.replace('_', ' ')}`, 'success');
            fetchAllInfluencers();
        } catch (error) {
            showAlert('Failed to update status', 'error');
        }
    };

    const handleStatusClick = (id, currentStatus) => {
        setCurrentInfluencerId(id);
        setSelectedStatus(currentStatus);
        setStatusRemark('');
        setStatusModalOpen(true);
    };

    const handleStatusModalUpdate = async () => {
        try {
            const { default: api } = await import('../services/api');
            await api.put(`/existing-influencers/${currentInfluencerId}/status`, {
                status: selectedStatus,
                remark: statusRemark
            });
            showAlert(`Status updated to ${selectedStatus.replace('_', ' ')}`, 'success');
            setStatusModalOpen(false);
            fetchAllInfluencers();
        } catch (error) {
            showAlert('Failed to update status', 'error');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'assigned': return 'primary';
            case 'in_progress': return 'warning';
            case 'not_interested': return 'error';
            case 'completed': return 'success';
            default: return 'default';
        }
    };

    const showAlert = (message, severity) => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 3000);
    };

    const handleCopyUrl = (rowId) => {
        const url = `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/${rowId}`;
        navigator.clipboard.writeText(url);
        toast.success('URL copied to clipboard!');
    };

    const handleSendEmailClick = (rowId) => {
        setSelectedInfluencerId(rowId);
        setEmailAddress('');
        setEmailModalOpen(true);
    };

    const handleSendEmail = async () => {
        if (!emailAddress.trim()) {
            toast.error('Please enter an email address');
            return;
        }
        setEmailSending(true);
        try {
            const { default: api } = await import('../services/api');
            const formUrl = `${process.env.REACT_APP_BASE_URL}/#/form/${user?.id}/${selectedInfluencerId}`;
            await api.post('/send-form-email', { email: emailAddress, formUrl: formUrl });
            setEmailModalOpen(false);
            setEmailAddress('');
            setSelectedInfluencerId('');
            toast.success('Email sent successfully!');
        } catch (error) {
            toast.error('Failed to send email');
        } finally {
            setEmailSending(false);
        }
    };

    const handleViewForm = (influencerFormId) => {
        if (influencerFormId) {
            window.open(`/influencer/${influencerFormId}`, '_blank');
        }
    };

    const handleExportToExcel = () => {
        let rows, sheetName;
        switch (tabValue) {
            case 0: rows = getAssignedRows(); sheetName = 'Assigned'; break;
            case 1: rows = getInProgressRows(); sheetName = 'In Progress'; break;
            case 2: rows = getNotInterestedRows(); sheetName = 'Not Interested'; break;
            case 3: rows = getCompletedRows(); sheetName = 'Completed'; break;
            default: rows = []; sheetName = 'Data';
        }

        const data = rows.map(row => ({
            'S.No': row.sno,
            'Username': row.username || '-',
            'Business Email': row.businessEmail || '-',
            'Name': row.name || '-',
            'Mobile': row.mobile || '-',
            'Profile Link': row.profileLink || '-',
            'Market (Language)': row.market || '-',
            'State': row.state || '-',
            'Assigned To': row.assignedTo || '-',
            'Given By': row.givenBy || '-',
            'Remark': row.remark || '-',
            'Status': row.status || '-',
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
        XLSX.writeFile(wb, `${sheetName}_Influencers_${new Date().toISOString().split('T')[0]}.xlsx`);
        toast.success('Data exported successfully!');
    };

    const filterByDate = (inf) => {
        if (!fromDate && !toDate) return true;
        if (!inf.updatedAt) return false;
        const updatedDate = dayjs(inf.updatedAt);
        if (fromDate && updatedDate.isBefore(dayjs(fromDate), 'day')) return false;
        if (toDate && updatedDate.isAfter(dayjs(toDate), 'day')) return false;
        return true;
    };

    const getUniqueLanguages = () => {
        const allInfluencers = [...assignedInfluencers, ...completedInfluencers, ...inProgressInfluencers, ...notInterestedInfluencers];
        const languages = allInfluencers
            .map(inf => inf.market)
            .filter(market => market && market !== null)
            .filter((value, index, self) => self.indexOf(value) === index);
        return languages;
    };

    const getUniqueStates = () => {
        const allInfluencers = [...assignedInfluencers, ...completedInfluencers, ...inProgressInfluencers, ...notInterestedInfluencers];
        const states = allInfluencers
            .map(inf => inf.state)
            .filter(state => state && state !== null)
            .filter((value, index, self) => self.indexOf(value) === index);
        return states;
    };

    const getAssignedRows = () => {
        return assignedInfluencers
            .filter(inf => !languageFilter || inf.market === languageFilter)
            .filter(inf => !stateFilter || inf.state === stateFilter)
            .filter(inf => !mobileFilter || (mobileFilter === 'with' ? inf.mobile : !inf.mobile))
            .filter(filterByDate)
            .map((inf, index) => ({
                ...inf,
                id: inf._id,
                sno: index + 1,
                mobile: inf.mobile || '-',
                state: inf.state || '-',
                businessEmail: inf.businessEmail || '-',
                remark: inf.remark || '-',
                assignedTo: inf.assignedTo ? (inf.assignedTo.name || inf.assignedTo.username) : '-',
                givenBy: inf.givenBy || '-',
                status: inf.status || 'assigned'
            }));
    };

    const getCompletedRows = () => {
        return completedInfluencers
            .filter(inf => !languageFilter || inf.market === languageFilter)
            .filter(inf => !stateFilter || inf.state === stateFilter)
            .filter(inf => !mobileFilter || (mobileFilter === 'with' ? inf.mobile : !inf.mobile))
            .filter(filterByDate)
            .map((inf, index) => ({
                ...inf,
                id: inf._id,
                sno: index + 1,
                mobile: inf.mobile || '-',
                state: inf.state || '-',
                businessEmail: inf.businessEmail || '-',
                remark: inf.remark || '-',
                assignedTo: inf.assignedTo ? (inf.assignedTo.name || inf.assignedTo.username) : '-',
                givenBy: inf.givenBy || '-',
            }));
    };

    const getInProgressRows = () => {
        return inProgressInfluencers
            .filter(inf => !languageFilter || inf.market === languageFilter)
            .filter(inf => !stateFilter || inf.state === stateFilter)
            .filter(inf => !mobileFilter || (mobileFilter === 'with' ? inf.mobile : !inf.mobile))
            .filter(filterByDate)
            .map((inf, index) => ({
                ...inf,
                id: inf._id,
                sno: index + 1,
                mobile: inf.mobile || '-',
                state: inf.state || '-',
                businessEmail: inf.businessEmail || '-',
                remark: inf.remark || '-',
                assignedTo: inf.assignedTo ? (inf.assignedTo.name || inf.assignedTo.username) : '-',
                givenBy: inf.givenBy || '-',
                status: inf.status || 'in_progress'
            }));
    };

    const getNotInterestedRows = () => {
        return notInterestedInfluencers
            .filter(inf => !languageFilter || inf.market === languageFilter)
            .filter(inf => !stateFilter || inf.state === stateFilter)
            .filter(inf => !mobileFilter || (mobileFilter === 'with' ? inf.mobile : !inf.mobile))
            .filter(filterByDate)
            .map((inf, index) => ({
                ...inf,
                id: inf._id,
                sno: index + 1,
                mobile: inf.mobile || '-',
                state: inf.state || '-',
                remark: inf.remark || '-',
                businessEmail: inf.businessEmail || '-',
                assignedTo: inf.assignedTo ? (inf.assignedTo.name || inf.assignedTo.username) : '-',
                givenBy: inf.givenBy || '-',
                status: inf.status || 'not_interested'
            }));
    };

    const assignedColumns = [
        { field: 'sno', headerName: 'S.No', width: 80 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'username', headerName: 'Username', width: 150 },
        { field: 'businessEmail', headerName: 'Business Email', width: 200 },
        { field: 'mobile', headerName: 'Mobile', width: 150 },
        { field: 'profileLink', headerName: 'Profile Link', width: 250 },
        { field: 'market', headerName: 'Market(Language)', width: 150 },
        { field: 'state', headerName: 'State', width: 150 },
        { field: 'assignedTo', headerName: 'Assigned To', width: 150 },
        { field: 'givenBy', headerName: 'Given By', width: 150 },
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => (
                <Chip
                    label={params.value?.replace('_', ' ') || 'assigned'}
                    color={getStatusColor(params.value)}
                    size="small"
                    sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
                    onClick={() => handleStatusClick(params.row.id, params.row.status)}
                />
            ),
        },
        { field: 'remark', headerName: 'Remark', width: 150 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={() => handleCopyUrl(params.row.id)}
                    >
                        Copy
                    </Button>
                    <Button
                        size="small"
                        startIcon={<Email />}
                        onClick={() => handleSendEmailClick(params.row.id)}
                    > Email
                    </Button>
                </Box>
            ),
        },
    ];

    const inProgressColumns = [
        { field: 'sno', headerName: 'S.No', width: 80 },
        { field: 'username', headerName: 'Username', width: 150 },
        { field: 'businessEmail', headerName: 'Business Email', width: 200 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'mobile', headerName: 'Mobile', width: 150 },
        { field: 'profileLink', headerName: 'Profile Link', width: 250 },
        { field: 'market', headerName: 'Market(Language)', width: 150 },
        { field: 'state', headerName: 'State', width: 150 },
        { field: 'assignedTo', headerName: 'Assigned To', width: 150 },
        { field: 'givenBy', headerName: 'Given By', width: 150 },
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => (
                <Chip
                    label={params.value?.replace('_', ' ') || 'in_progress'}
                    color={getStatusColor(params.value)}
                    size="small"
                    sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
                    onClick={() => handleStatusClick(params.row.id, params.row.status)}
                />
            ),
        },
        { field: 'remark', headerName: 'Remark', width: 150 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 200,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        startIcon={<ContentCopy />}
                        onClick={() => handleCopyUrl(params.row.id)}
                    >
                        Copy
                    </Button>
                    <Button
                        size="small"
                        startIcon={<Email />}
                        onClick={() => handleSendEmailClick(params.row.id)}
                    >
                        Email
                    </Button>
                </Box>
            ),
        },
    ];

    const notInterestedColumns = [
        { field: 'sno', headerName: 'S.No', width: 80 },
        { field: 'username', headerName: 'Username', width: 150 },
        { field: 'businessEmail', headerName: 'Business Email', width: 200 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'mobile', headerName: 'Mobile', width: 150 },
        { field: 'profileLink', headerName: 'Profile Link', width: 250 },
        { field: 'market', headerName: 'Market(Language)', width: 150 },
        { field: 'state', headerName: 'State', width: 150 },
        { field: 'assignedTo', headerName: 'Assigned To', width: 150 },
        { field: 'givenBy', headerName: 'Given By', width: 150 },
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => (
                <Chip
                    label={params.value?.replace('_', ' ') || 'not_interested'}
                    color={getStatusColor(params.value)}
                    size="small"
                    sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
                    onClick={() => handleStatusClick(params.row.id, params.row.status)}
                />
            ),
        },
        { field: 'remark', headerName: 'Remark', width: 150 },
    ];

    const completedColumns = [
        { field: 'sno', headerName: 'S.No', width: 80 },
        { field: 'username', headerName: 'Username', width: 150 },
        { field: 'businessEmail', headerName: 'Business Email', width: 200 },
        { field: 'name', headerName: 'Name', width: 150 },
        { field: 'mobile', headerName: 'Mobile', width: 150 },
        { field: 'profileLink', headerName: 'Profile Link', width: 250 },
        { field: 'market', headerName: 'Market(Language)', width: 150 },
        { field: 'state', headerName: 'State', width: 150 },
        { field: 'assignedTo', headerName: 'Assigned To', width: 150 },
        { field: 'givenBy', headerName: 'Given By', width: 150 },
        {
            field: 'status',
            headerName: 'Status',
            width: 150,
            renderCell: (params) => (
                <Chip
                    label="Completed"
                    color="success"
                    size="small"
                    sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
                    onClick={() => handleStatusClick(params.row.id, 'completed')}
                />
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            renderCell: (params) => (
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<Visibility />}
                    onClick={() => navigate(`/influencer/${params?.row?.influencerFormId?._id || params?.row?.influencerFormId}`)}
                    disabled={!params.row.influencerFormId}
                >
                    View
                </Button>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                My Influencers
            </Typography>

            {alert.show && (
                <Alert severity={alert.severity} sx={{ mb: 2 }}>
                    {alert.message}
                </Alert>
            )}

            <Card>
                <CardContent>
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Filter by Market</InputLabel>
                            <Select
                                value={languageFilter}
                                label="Filter by Market"
                                onChange={(e) => setLanguageFilter(e.target.value)}
                            >
                                <MenuItem value="">All Languages</MenuItem>
                                {getUniqueLanguages().map((language) => (
                                    <MenuItem key={language} value={language}>
                                        {language}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Filter by State</InputLabel>
                            <Select
                                value={stateFilter}
                                label="Filter by State"
                                onChange={(e) => setStateFilter(e.target.value)}
                            >
                                <MenuItem value="">All States</MenuItem>
                                {getUniqueStates().map((state) => (
                                    <MenuItem key={state} value={state}>
                                        {state}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Filter by Mobile</InputLabel>
                            <Select
                                value={mobileFilter}
                                label="Filter by Mobile"
                                onChange={(e) => setMobileFilter(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="with">With Mobile</MenuItem>
                                <MenuItem value="without">Without Mobile</MenuItem>
                            </Select>
                        </FormControl>
                        <Box>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    label="From Date"
                                    value={fromDate}
                                    onChange={(newValue) => setFromDate(newValue)}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: 'small', sx: { minWidth: 100, mr: 2 } } }}
                                />
                                <DatePicker
                                    label="To Date"
                                    value={toDate}
                                    onChange={(newValue) => setToDate(newValue)}
                                    format="DD/MM/YYYY"
                                    slotProps={{ textField: { size: 'small', sx: { minWidth: 100 } } }}
                                />
                            </LocalizationProvider>
                        </Box>
                    </Box>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant={tabValue === 0 ? 'contained' : 'text'}
                                    onClick={() => setTabValue(0)}
                                    sx={{ color: 'black' }}
                                >
                                    Assigned ({getAssignedRows().length})
                                </Button>
                                <Button
                                    variant={tabValue === 1 ? 'contained' : 'text'}
                                    onClick={() => setTabValue(1)}
                                    sx={{ color: 'black' }}
                                >
                                    In Progress ({getInProgressRows().length})
                                </Button>
                                <Button
                                    variant={tabValue === 2 ? 'contained' : 'text'}
                                    onClick={() => setTabValue(2)}
                                    sx={{ color: 'black' }}
                                >
                                    Not Interested ({getNotInterestedRows().length})
                                </Button>
                                <Button
                                    variant={tabValue === 3 ? 'contained' : 'text'}
                                    onClick={() => setTabValue(3)}
                                    sx={{ color: 'black' }}
                                >
                                    Completed ({getCompletedRows().length})
                                </Button>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<Download />}
                                onClick={handleExportToExcel}
                            >
                                Export to Excel
                            </Button>
                        </Box>
                    </Box>

                    <TabPanel value={tabValue} index={0}>
                        <DataGrid
                            rows={getAssignedRows()}
                            columns={assignedColumns}
                            pageSize={10}
                            loading={loading}
                            autoHeight
                            getRowId={(row) => row.id}
                        />
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <DataGrid
                            rows={getInProgressRows()}
                            columns={inProgressColumns}
                            pageSize={10}
                            loading={loading}
                            autoHeight
                            getRowId={(row) => row.id}
                        />
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <DataGrid
                            rows={getNotInterestedRows()}
                            columns={notInterestedColumns}
                            pageSize={10}
                            loading={loading}
                            autoHeight
                            getRowId={(row) => row.id}
                        />
                    </TabPanel>

                    <TabPanel value={tabValue} index={3}>
                        <DataGrid
                            rows={getCompletedRows()}
                            columns={completedColumns}
                            pageSize={10}
                            loading={loading}
                            autoHeight
                            getRowId={(row) => row.id}
                        />
                    </TabPanel>
                </CardContent>
            </Card>

            <Dialog open={emailModalOpen} onClose={() => setEmailModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Send Form URL via Email</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={emailAddress}
                        onChange={(e) => setEmailAddress(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmailModalOpen(false)} disabled={emailSending}>Cancel</Button>
                    <Button onClick={handleSendEmail} variant="contained" disabled={emailSending} sx={{ bgcolor: '#5386e4' }}>
                        {emailSending ? 'Sending...' : 'Send Email'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={statusModalOpen} onClose={() => setStatusModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Update Status</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={selectedStatus}
                            label="Status"
                            onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                            <MenuItem value="assigned">Assigned</MenuItem>
                            <MenuItem value="in_progress">In Progress</MenuItem>
                            <MenuItem value="not_interested">Not Interested</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        label="Remark"
                        multiline
                        rows={3}
                        value={statusRemark}
                        onChange={(e) => setStatusRemark(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStatusModalOpen(false)}>Cancel</Button>
                    <Button onClick={handleStatusModalUpdate} variant="contained">
                        Update
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminInfluencers;
